import React, { KeyboardEventHandler, ReactNode, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { BaseModal } from '@repo/components/modals';
import Button from '@components/Button';
import styles from './ShareContact.module.scss';
import ShareeRow from './ShareeRow';
import PublicShareStatus from './PublicShareStatus';
import CopyLinkBtn from './CopyLinkBtn';
import validEmail from '@repo/utils/helpers/valid-email';
import { ShareStatus } from '@repo/utils/models/ShareStatus';
import { SharedResourceType } from '@repo/redux/reducers/data/shareStatuses';
import { Sharee } from '@repo/utils/models/Sharee';

export default function ShareResourceModal<P>({
  closeModal,
  sharedResourceType,
  sharedResourcePill,
  shareStatus,
  sharePublicly,
  unsharePublicly,
  addPublicPermission,
  removePublicPermission,
  shareWithEmail,
  unshareWithSharee,
  addShareePermission,
  removeShareePermission,
}: {
  closeModal: () => void;
  sharedResourceType: SharedResourceType;
  shareStatus: ShareStatus<P>;
  sharedResourcePill: ReactNode;
  sharePublicly: () => void;
  unsharePublicly: () => void;
  addPublicPermission: (permission: P) => void;
  removePublicPermission: (permission: P) => void;
  shareWithEmail: (email: string) => void;
  unshareWithSharee: (sharee: Sharee<P>) => void;
  addShareePermission: (sharee: Sharee<P>, permission: P) => void;
  removeShareePermission: (sharee: Sharee<P>, permission: P) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [emailInputInvalid, setEmailInputInvalid] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const emailIsValid = validEmail(inviteeEmail);

  const inviteEnteredEmail = () => {
    shareWithEmail(inviteeEmail);
    setInviteeEmail('');
  };

  const onEmailInputBlur = () => {
    if (!!inviteeEmail) {
      setEmailInputInvalid(!emailIsValid);
    }
    setInputFocused(false);
  };

  const saveOnEnter: KeyboardEventHandler = (e) => {
    if (e.key == 'Enter' && emailIsValid) {
      inviteEnteredEmail();
    }
  };

  const makeProfilePublic = () => {
    if (shareStatus.isLoaded && !shareStatus.publiclyShared) {
      sharePublicly();
    }
  };

  useEffect(() => {
    if (emailInputInvalid) {
      setEmailInputInvalid(false);
    }
  }, [inviteeEmail]);

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  }, []);

  return (
    <BaseModal className={styles.contactShareModal} isOpen={true} onRequestClose={closeModal}>
      <div className={cn(styles.header, 'modal-header')}>
        Share
        {sharedResourcePill}
      </div>
      <div className={styles.contents}>
        <div className={styles.emailInviteInputRow}>
          <input
            className={cn('form-input', styles.emailInput, emailInputInvalid && 'invalid')}
            ref={inputRef}
            type="email"
            value={inviteeEmail}
            onChange={(e) => setInviteeEmail(e.target.value)}
            onBlur={onEmailInputBlur}
            onFocus={() => setInputFocused(true)}
            onKeyDown={saveOnEnter}
            placeholder="Enter an email address"
            autoFocus
          />
          <Button
            className={cn('btn btn-primary', inputFocused && emailIsValid && styles.btnFocused)}
            onClick={inviteEnteredEmail}
            disabled={!emailIsValid}
          >
            Invite
          </Button>
        </div>
        <div className={styles.permissionsGrid}>
          <PublicShareStatus
            sharedResourceType={sharedResourceType}
            shareStatus={shareStatus}
            sharePublicly={sharePublicly}
            unsharePublicly={unsharePublicly}
            addPublicPermission={addPublicPermission}
            removePublicPermission={removePublicPermission}
          />
          {shareStatus.sharees
            .valueSeq()
            .toArray()
            .map((sharee) => (
              <ShareeRow
                key={sharee.id}
                sharedResourceType={sharedResourceType}
                sharee={sharee}
                addPermission={(permission: P) => addShareePermission(sharee, permission)}
                removePermission={(permission: P) => removeShareePermission(sharee, permission)}
                unshare={() => unshareWithSharee(sharee)}
              />
            ))}
        </div>
      </div>
      <div className={styles.footer}>
        <CopyLinkBtn
          link={`${window.location.origin}/share/${sharedResourceType}/${shareStatus.uuid}`}
          onCopy={makeProfilePublic}
        />
      </div>
    </BaseModal>
  );
}
